import { Injectable } from '@angular/core';
import { LabelModel } from './models/labels/label.model';
import { AnnotationModel } from './models/labels/annotation.model';
import { ChapterModel } from './models/chapter.model';
import { uuid } from '../shared/uuid';
import { LabelPinType } from './models/labels/label-pin-type.enum';
import { LabelThemeEnum } from './models/labels/label-theme.enum';
import { HotspotModel } from './models/labels/hotspot.model';
import { HotspotThemesEnum } from './models/labels/hotspot.themes.enum';
import { LabelStateModel } from './models/labels/label-state.model';
import { FlyToObjectErrandModel } from './models/errands/fly-to-object-errand.model';
import { FlyToCameraErrandModel } from './models/errands/fly-to-camera-errand.model';
import { ErrandInterface } from './models/errands/errand.model';
import { classToClass } from 'class-transformer';
import { HotspotTypeEnum } from './models/labels/hotspot.types.enum';
import { LabelFlyTo } from './models/labels/label-fly-to.enum';
import { Human } from '../engine/models/human.model';
import { EngineService } from '../engine/engine.service';
import { CameraPositionComponent } from '../edit/components/camera-position/camera-position.component';
import { ErrandType } from './models/errands/errand-type.enum';
import { FlyToChapterErrandModel } from './models/errands/fly-to-chapter-errand.model';


const errandTypeToHotspotAction = {
  'flyToCamera': 'flyTo',
  'flyToChapter': 'flyTo'
};

function translateLabelActionToHotspot(type): string {
  return errandTypeToHotspotAction[type] || type;
}


/*
Handles some ugliness in the two-way conversion of legacy annotations and
hotspots into newer labels.
*/

@Injectable({
  providedIn: 'root'
})
export class LabelsService {
  constructor(
    private engineService: EngineService
  ) { }

  // DESERIALIZATION =====================================================================

  // returns true if hotspot can be interpreted as a label (ignore show/hide hotspot)
  filterHotspot(moduleData: any, chapter: any, hotspot: HotspotModel): boolean {
    const supportedActions = new RegExp('^(flyTo|flyToObject|hideHotspot|showHotspot|resetHotspots)');
    // get filtered actions
    const actions = this.getHotspotAction(moduleData, chapter, hotspot);
    const filtered = actions.filter(_action => supportedActions.test(_action));
    if (actions.length === filtered.length) {
      return true;
    } else {
      return false;
    }
  }

  // deserialize legacy annotations into labels
  deserializeAnnotation(annotation: AnnotationModel, chapter: ChapterModel, theme: LabelThemeEnum): LabelModel {
    const label = new LabelModel(annotation.title || '');

    // identifyin information
    label.id = annotation.annotationId || uuid.idWithPrefix('label');
    label.chapterId = chapter.id;

    // style info
    label.expandTag = true;
    label.showDescription = true;
    label.collapseDescription = false;
    label.pinGlow = false;
    label.hideWire = false;
    label.hideTag = false;
    if (theme === LabelThemeEnum.Annotation) {
      label.theme = LabelThemeEnum.Annotation;
      label.pinType = LabelPinType.Default;
      label.flyTo = false;
      label.states = [];
    } else {
      // annotation -> studio label
      label.theme = LabelThemeEnum.StudioNext;
      label.pinType = LabelPinType.PlusMinus;
      label.flyTo = true;
      label.states = [
        new LabelStateModel(),
        new LabelStateModel()
      ];

      label.states[0].errands = [new FlyToCameraErrandModel(chapter.camera.location)];
      label.states[1].errands = [new FlyToObjectErrandModel(annotation.objectId)];
    }
    label.occludable = annotation.occludable || true;
    label.renderableIndex = annotation.renderableIndex || 0;

    if (annotation.objectId && annotation.hasOwnProperty('barycentric')) {
      // use barycentric coordinate
      label.objectId = annotation.objectId;
      label.primitiveIndex = annotation.primitiveIndex;
      label.barycentric = annotation.barycentric;
    } else if (annotation.hasOwnProperty('pos')) {
      // world position (let the marker deal with it)
      if (annotation.hasOwnProperty('objectId')) {
        label.objectId = annotation.objectId;
      }
      label.pos = annotation.pos;
    }

    // direction
    if (annotation.dir && Array.isArray(annotation.dir)) {
      label.dir = annotation.dir as [number, number, number];
    } else if (annotation.dir && annotation.dir instanceof Object) {
      label.dir = Object.values(annotation.dir) as [number, number, number];
    }

    // text label information
    label.description = annotation.description || '';
    label.offset = annotation.labelOffset || [40, 40];

    return label;
  }

  // converts one (or two, if paired)  hotspots into a label
  deserializeHotspots(moduleData: any, chapter: any, chapterIndex: number, ...hotspots: HotspotModel[]): LabelModel {
    const [primaryHotspot, secondaryHotspot] = hotspots;

    // label identification
    const label = new LabelModel(primaryHotspot.title || '');
    label.id = uuid.idWithPrefix('hotspot');
    label.objectId = primaryHotspot.objectId;
    label.chapterId = chapter.id;

    // display information
    label.description = primaryHotspot.description || '';
    label.pinType = /plus|minus/.test(primaryHotspot.className) ? LabelPinType.PlusMinus : LabelPinType.Default;
    label.pinGlow = /pulse/.test(primaryHotspot.className);
    label.occludable = true;
    label.shown = primaryHotspot.shown;
    label.offset = primaryHotspot.labelOffset ? primaryHotspot.labelOffset : [22, -17];

    if (/left/.test(primaryHotspot.className) && label.offset[0] > 0) {
      label.offset[0] *= -1;
    } else if (/right/.test(primaryHotspot.className) && label.offset[0] < 0) {
      label.offset[0] *= -1;
    }

    // positioning
    if (primaryHotspot.hasOwnProperty('barycentric')) {
      // use shape centric coordinate
      label.renderableIndex = primaryHotspot.renderableIndex || 0;
      label.barycentric = primaryHotspot.barycentric;
      label.primitiveIndex = primaryHotspot.primitiveIndex;
    } else {
      // use world pstiion (let marker handle positioning)
      label.pos = primaryHotspot.pos;
    }
    label.dir = primaryHotspot.dir;


    // diplay theme:
    primaryHotspot.theme = !!primaryHotspot.theme ? primaryHotspot.theme : HotspotThemesEnum.classicHotspot;
    switch (primaryHotspot.theme) {
      case HotspotThemesEnum.classicAnnotation:
        label.theme = LabelThemeEnum.Annotation;
        label.pinType = LabelPinType.Default; // force set to circle if annotation
        label.showDescription = true;
        label.collapseDescription = false;
        label.expandTag = true;
        label.hideTag = false;
        label.hideWire = false;
        break;
      case HotspotThemesEnum.classicHotspot:
        label.theme = LabelThemeEnum.Classic;
        label.showDescription = /label/.test(primaryHotspot.className);
        label.expandTag = /label/.test(primaryHotspot.className);
        label.hideTag = !label.expandTag;
        label.collapseDescription = false; // default to show description
        label.hideWire = !/leader-line/.test(primaryHotspot.className);
        break;
      case HotspotThemesEnum.studioNext:
        label.theme = LabelThemeEnum.StudioNext;
        label.showDescription = !!primaryHotspot.description;
        label.collapseDescription = /collapsible/g.test(primaryHotspot.className);
        label.hideTag = !primaryHotspot.title;
        label.hideWire = !/leader-line/.test(primaryHotspot.className);
        break;
      default:
        break;
    }

    // actions (first action)
    label.flyTo = false;
    label.flyToTarget = null;
    label.states = [];

    const actions1 = this.filterHotspotActions(this.getHotspotAction(moduleData, chapter, primaryHotspot));
    const actions2 = !!secondaryHotspot ? this.filterHotspotActions(this.getHotspotAction(moduleData, chapter, secondaryHotspot)) : [];
    const state1 = new LabelStateModel();
    const state2 = new LabelStateModel();

    if (actions1.length) {
      state1.errands = actions1.map((action) => this.convertActionToErrand(moduleData, chapterIndex, action));
    }
    if (actions2.length) {
      state2.errands = actions2.map((action) => this.convertActionToErrand(moduleData, chapterIndex, action));
    }
    label.states = [state2, state1];
    return label;
  }

  // CONVERSION ===========================================================================
  makeDefaultStudioLabel(sourceLabel?: LabelModel) {
    const label = sourceLabel ? { ...sourceLabel } : new LabelModel('');

    label.pinType = LabelPinType.PlusMinus;
    label.theme = LabelThemeEnum.StudioNext;

    label.expandTag = true;
    label.showDescription = true;
    label.collapseDescription = false;

    label.pinGlow = false;

    label.hideWire = false;
    label.hideTag = false;

    label.states = [
      new LabelStateModel(),
      new LabelStateModel()
    ];


    return label;
  }

  makeAnnotationLabel(sourceLabel?: LabelModel) {
    const label = sourceLabel ? { ...sourceLabel } : new LabelModel('');

    label.pinType = LabelPinType.Default;
    label.theme = LabelThemeEnum.Annotation;

    label.expandTag = true;
    label.showDescription = true;
    label.collapseDescription = false;

    label.pinGlow = false;

    label.hideWire = false;
    label.hideTag = false;

    label.states = [];

    return label;
  }

  // SERIALIZATION ========================================================================
  // converts one label into one (or two, if paired) hotspots
  serializeLabel(
    label: LabelModel,
    idx: number, 
    reverseNamespaces?: {[renamespaceId: string]: string}
    ): { hotspots: HotspotModel[], data: any } {

    const hotspot = new HotspotModel();
    const labelCopy = classToClass(label);

    // basic
    const className = ['circle', 'draggable'];
    hotspot.shown = true;

    // identification
    if (label.objectId) {
      hotspot.objectId = labelCopy.objectId;
    }

    // position and direction. #TODO: [EM] support hotspot barycentric positioning?
    if (label.primitiveIndex && label.barycentric && label.objectId) {
      hotspot.renderableIndex = label.renderableIndex || 0;
      hotspot.primitiveIndex = label.primitiveIndex;
      hotspot.barycentric = label.barycentric;
      hotspot.pos = this.engineService.barycentricToWorld(
        reverseNamespaces ? reverseNamespaces[label.objectId] : label.objectId,
        label.primitiveIndex,
        label.barycentric
      );
    } else {
      hotspot.pos = labelCopy.pos;
    }

    if (!!label.dir) {
      // NOTE: The direction vector is a float-32-array
      hotspot.dir = Array.prototype.slice.call(label.dir);
    }
    hotspot.labelOffset = label.offset;


    // display information
    switch (label.theme) {
      case LabelThemeEnum.Annotation:
        hotspot.theme = HotspotThemesEnum.classicAnnotation;
        break;
      case LabelThemeEnum.Classic:
        hotspot.theme = HotspotThemesEnum.classicHotspot;
        break;
      default:
        hotspot.theme = HotspotThemesEnum.studioNext;
    }

    if (labelCopy.title !== '') {
      hotspot.title = labelCopy.title;
      hotspot.description = labelCopy.description;
      hotspot.type = HotspotTypeEnum.CircleLabel;
    } else {
      hotspot.type = HotspotTypeEnum.Circle;
    }

    // hotspot classes
    if (label.pinGlow) {
      className.push('pulse');
    }
    if (label.pinType === LabelPinType.PlusMinus) {
      className.push('plus');
    }
    if (!label.hideWire) {
      className.push('leader-line');
    }
    if (!label.hideTag) {
      className.push('label');
    }
    if (label.collapseDescription) {
      className.push('collapsible collapsed');
    }
    hotspot.className = className.join(' ');

    // hotspot actions & data
    let data = {}; // customData blocked to current chapter, used by hotspots for action targets
    hotspot.actions = [];
    const hotspots = [hotspot];
    const stateLength = label.states.length;
    label.states.forEach((state, stateIndex) => {
      // NOTE: [EM] a little extra complex here b/c states and hotspot actions are kind of 'inverses' of eachother.
      if (stateIndex === stateLength - 1) {
        // add actions to this hotspot
        hotspots[0].actions.push('resetHotspots');
        state.errands.forEach((errand) => {
          const action = this.convertErrandToAction(label, errand);
          hotspots[0].actions.push(action.action);
          data = { ...data, ...action.data };
        });
      } else {
        // we need new hotspots
        const secondaryHotspot = classToClass(hotspot);
        secondaryHotspot.actions = [];
        secondaryHotspot.className = secondaryHotspot.className.replace('plus', 'minus');
        state.errands.forEach((errand) => {
          const action = this.convertErrandToAction(label, errand);
          secondaryHotspot.actions.push(action.action);
          data = { ...data, ...action.data };
        });
        hotspots.push(secondaryHotspot);
      }
    });

    // teach plus two hotspots to toggle
    if (hotspots.length === 2) {

      // hotspot 1 : hide 1, show 2
      hotspots[0].actions.push(`hideHotspots:currentChapter.hotspots[${idx}]`);
      hotspots[0].actions.push(`showHotspots:currentChapter.hotspots[${idx + 1}]`);

      // hotspot 2: hide 2, show 1
      hotspots[1].actions.push(`hideHotspots:currentChapter.hotspots[${idx + 1}]`);
      hotspots[1].actions.push(`showHotspots:currentChapter.hotspots[${idx}]`);

      hotspots[0].shown = true;
      hotspots[1].shown = false;
    }

    if (hotspots.length === 2 && label.collapseDescription) {
      hotspots[1].className += hotspots[1].className.replace('collapsed', '');
    }


    // ensure left/right class
    hotspots.forEach((_hotspot) => {
      if (hotspot.labelOffset[0] < 0) {
        _hotspot.className = _hotspot.className.replace('right', '') + ' left';
      } else if (hotspot.labelOffset[0] > 0) {
        _hotspot.className = _hotspot.className.replace('left', '') + ' right';
      }
    });

    return {
      data: data,
      hotspots: hotspots
    };

  }

  // HELPER FUNCTIONS ======================================================================

  // parse out actions array from hotspot, given module and chapter scopes
  private getHotspotAction(moduleData: any, chapter: any, hotspot: HotspotModel): string[] {
    const actions = !!hotspot.actionSrc ? this.objectAddress(moduleData, hotspot.actionSrc) : (hotspot.actions ? hotspot.actions : []);
    if (actions instanceof Array) {
      return actions;
    } else {
      return [];
    }
  }

  // filter for hotspot actions that are supported in studio
  private filterHotspotActions(actions: Array<any>) {
    const supportedActions = new RegExp('^(flyTo|flyToObject)');
    return actions.filter((_action) => supportedActions.test(_action));
  }

  // convert legacy action into equivalent errand
  private convertActionToErrand = (moduleData, chapterIndex, action) => {
    const flyToAction = new RegExp('^flyTo(Async)?');
    const flyToObjectAction = new RegExp('^flyToObject(Async)?');
    const flyToChapterAction = new RegExp('^flyTo[\w\.\:]+currentChapter\.[flyTo|jumpTo]');

    // currently we only support flyTo
    const tokens = action.split(':').slice(0, 2); // we only take action and one parameter

    // conditionally parse where in the data the action target value is located
    const actionScope = moduleData;
    let actionTarget = tokens[1]; // address is scoped to module data
    if (/currentChapter/.test(actionTarget)) {
      actionTarget = actionTarget.replace('currentChapter', `chapters[${chapterIndex}]`);
    }

    // build errand based on action
    let errand = null;

    if (flyToObjectAction.test(tokens[0])) {
      errand = new FlyToObjectErrandModel(this.objectAddress(actionScope, actionTarget));
    } else if (flyToAction.test(tokens[0])) {
      errand = new FlyToCameraErrandModel(this.objectAddress(actionScope, actionTarget));
    } else if (flyToChapterAction.test(tokens[0])) {
      errand = new FlyToChapterErrandModel();
    }

    return errand;
  }

  // convert errand into equivalent legacy action
  private convertErrandToAction = (label: LabelModel, errand: ErrandInterface): { action: string, data: any } => {
    const id = uuid.idWithPrefix('errand').replace(/\-/g, '_');
    const data = {};
    let action = '';
    const type = translateLabelActionToHotspot(errand.type);

    if (errand.type === ErrandType.flyToChapter) {
      // there is no flyToChapter action. We have to pass in the address to the chapter's camera (jupTy or flyTo) property
      action = `${type}Async:currentChapter.${this.engineService.getChapter(label.chapterId).camera.action}`;
    } else {
      action = `${type}Async:currentChapter.customData.${id}`,
        data[id] = errand.value;
    }

    return {
      action: action,
      data: data
    };
  }

  // extract sub-object from object given dot-notation address
  private objectAddress = (srcObject: any, address: string) => {
    return address.split('.').reduce((o, i) => {
      if (o === null) {
        return null;
      } else if (/\[+[0-9+]\]+/.test(i)) {
        // access array element
        const parts = i.split('[');
        const idx = Number(parts[1].match(/[0-9]+/));
        if (parts[0] in o && idx in o[parts[0]]) {
          return o[parts[0]][idx];
        } else {
          return null;
        }
      } else if (i in o) {
        return o[i];
      } else {
        return null;
      }
    }, srcObject);
  }
}


